import React, { useEffect } from "react";
import Navbar from "../components/landing/Navbar";
import Footer from "../components/landing/Footer";
import Seo, { breadcrumbJsonLd } from "../components/Seo";
import { PAGE_SEO, CONTACT_KEYWORDS, WA_LINKS } from "../data/siteData";
import { GOOGLE_FORM_URL } from "../config";
import { ArrowUpRight, MessageCircle } from "lucide-react";

const EXPECTATIONS = [
  "Initial fit check through WhatsApp",
  "Response target: within one business day",
  "No payment required to check fit",
  "A recommendation is not a free strategy engagement",
  "Project scheduling begins after scope approval, agreement, and deposit",
];

export default function ContactPage() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div data-testid="contact-page" className="bg-white">
      <Seo
        {...PAGE_SEO.contact}
        jsonLd={breadcrumbJsonLd([
          { name: "Home", path: "/" },
          { name: "Contact", path: "/contact" },
        ])}
      />
      <Navbar />

      <section className="relative bg-[#0A192F] text-white pt-32 pb-16 md:pt-40 md:pb-20">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="flex items-center gap-3 mb-5">
            <span className="h-px w-10 bg-[#D4AF37]" />
            <span className="text-[11px] uppercase tracking-[0.3em] text-[#D4AF37] font-semibold">
              Contact AXIS
            </span>
          </div>
          <h1
            data-testid="contact-headline"
            className="font-display text-4xl sm:text-5xl lg:text-6xl tracking-tighter font-semibold leading-[1.0] max-w-3xl"
          >
            Tell us what you are <span className="text-[#D4AF37]">trying to improve.</span>
          </h1>
          <p className="mt-6 text-base md:text-lg text-[#C0C0C0] leading-relaxed max-w-2xl">
            You do not need to understand every AXIS service before contacting us. Start with
            the business problem. We will ask three short questions and recommend one
            appropriate next step.
          </p>
          <div className="mt-9">
            <a
              href={WA_LINKS.recommend}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="contact-hero-cta"
              className="inline-flex items-center justify-center gap-2 bg-[#D4AF37] text-[#0A192F] px-7 py-3.5 text-sm md:text-base font-semibold border border-[#D4AF37] hover:bg-transparent hover:text-[#D4AF37] transition-all duration-300"
            >
              Message AXIS on WhatsApp →
            </a>
          </div>
        </div>
      </section>

      <section data-testid="contact-keywords" className="bg-[#F8F9FA] py-20 md:py-28">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <h2 className="font-display text-2xl md:text-3xl font-semibold text-[#0A192F] mb-10">
            Service keywords
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {CONTACT_KEYWORDS.map((k) => (
              <a
                key={k.keyword}
                href={k.link}
                target="_blank"
                rel="noopener noreferrer"
                data-testid={`contact-keyword-${k.keyword.toLowerCase()}`}
                className="group bg-white border border-[#C0C0C0]/40 p-6 flex items-start gap-4 hover:border-[#D4AF37] transition-colors"
              >
                <div className="w-11 h-11 bg-[#0A192F] flex items-center justify-center flex-shrink-0">
                  <MessageCircle className="w-5 h-5 text-[#D4AF37]" />
                </div>
                <div>
                  <div className="font-display text-lg font-semibold text-[#0A192F] group-hover:text-[#1E3A8A] transition-colors">
                    Message “{k.keyword}”
                  </div>
                  <p className="mt-1 text-sm text-[#475569]">{k.desc}</p>
                </div>
              </a>
            ))}
          </div>
        </div>
      </section>

      <section data-testid="contact-expectations" className="bg-white py-20 md:py-24">
        <div className="max-w-7xl mx-auto px-6 md:px-12 grid grid-cols-1 lg:grid-cols-2 gap-12">
          <div>
            <h2 className="font-display text-2xl md:text-3xl font-semibold text-[#0A192F]">
              What to expect
            </h2>
            <ul className="mt-6 space-y-3">
              {EXPECTATIONS.map((item) => (
                <li key={item} className="flex items-start gap-2.5 text-sm md:text-base text-[#0A192F]">
                  <span className="w-1.5 h-1.5 bg-[#D4AF37] rotate-45 mt-2 flex-shrink-0" />
                  {item}
                </li>
              ))}
            </ul>
          </div>
          <div className="bg-[#F8F9FA] border border-[#C0C0C0]/40 p-8 h-fit">
            <h3 className="font-display text-xl font-semibold text-[#0A192F]">
              Prefer not to use WhatsApp?
            </h3>
            <p className="mt-3 text-sm text-[#475569] leading-relaxed">
              You can also complete a short intake form and AXIS will follow up within one
              business day.
            </p>
            <a
              href={GOOGLE_FORM_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="contact-form-fallback"
              className="mt-6 inline-flex items-center justify-center gap-2 bg-[#0A192F] text-white px-6 py-3 text-sm font-semibold border border-[#0A192F] hover:bg-transparent hover:text-[#0A192F] transition-all duration-300"
            >
              Check My Fit <ArrowUpRight className="w-4 h-4" />
            </a>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
